import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Title } from 'react-admin';
import { getErrorMessage } from 'shared/utils/get-error-message';
import {
  DEFAULT_COURSE_LIST_FILTERS,
  CourseListFilters,
  filterCourses,
} from './lib/filter-courses';
import { formatStatus, formatTableDateTime, isCourseArchived } from './lib/formatters';
import { buildReorderItems, getAccessLabel, getCompanyNames } from './lib/reorder';
import {
  actionButtonColumnSx,
  actionColumnStackSx,
  actionReorderRowSx,
  contentCardSx,
  contentTableContainerSx,
  contentTableSx,
  courseTableCellSx,
  reorderButtonSx,
} from './lib/table-styles';
import {
  CompanyItem,
  CuratorItem,
  ContentCourseItem,
  CourseArchiveFilter,
  DEFAULT_COURSE_FORM,
  CourseForm,
  archiveCourse,
  loadCompanies,
  loadCurators,
  loadCourses,
  reorderCourses,
  restoreCourse,
  saveCourse,
} from './model';
import { CourseFormDialog } from './ui/course-form-dialog';
import { CopyIdButton } from './ui/copy-id-button';
import { ConfirmDeleteDialog } from './ui/folder-form-dialog';
import { ContentCourseDetail } from './course-detail';

const getCourseIdFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  return params.get('courseId');
};

export const Content = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const courseId = getCourseIdFromSearch(location.search);

  const [courses, setCourses] = useState<ContentCourseItem[]>([]);
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [curators, setCurators] = useState<CuratorItem[]>([]);
  const [curatorsLoadError, setCuratorsLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isCourseDialogOpen, setIsCourseDialogOpen] = useState(false);
  const [courseForm, setCourseForm] = useState<CourseForm>(DEFAULT_COURSE_FORM);
  const [courseFormError, setCourseFormError] = useState<string | null>(null);
  const [isCourseSaving, setIsCourseSaving] = useState(false);

  const [archiveCourseId, setArchiveCourseId] = useState<string | null>(null);
  const [archiveCourseError, setArchiveCourseError] = useState<string | null>(null);
  const [isCourseArchiving, setIsCourseArchiving] = useState(false);
  const [restoringCourseId, setRestoringCourseId] = useState<string | null>(null);
  const [filters, setFilters] = useState<CourseListFilters>(DEFAULT_COURSE_LIST_FILTERS);
  const [archiveFilter, setArchiveFilter] = useState<CourseArchiveFilter>('active');

  const filteredCourses = useMemo(() => filterCourses(courses, filters), [courses, filters]);
  const activeCourses = useMemo(
    () => courses.filter((course) => !isCourseArchived(course)),
    [courses],
  );

  const refreshCourses = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await loadCourses(1, archiveFilter);
      setCourses(response.data || []);
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Ошибка загрузки курсов'));
    } finally {
      setIsLoading(false);
    }
  }, [archiveFilter]);

  useEffect(() => {
    if (courseId) return;

    refreshCourses();
    loadCompanies()
      .then((response) => setCompanies(response.data || []))
      .catch(() => setCompanies([]));
    loadCurators()
      .then((response) => {
        setCurators(response.data || []);
        setCuratorsLoadError(null);
      })
      .catch(() => {
        setCurators([]);
        setCuratorsLoadError('Не удалось загрузить список кураторов');
      });
  }, [courseId, refreshCourses]);

  const openCourse = (id: string) => {
    navigate(`/content?courseId=${id}`);
  };

  const openCreateCourse = () => {
    setCourseForm(DEFAULT_COURSE_FORM);
    setCourseFormError(null);
    setIsCourseDialogOpen(true);
  };

  const openEditCourse = (course: ContentCourseItem) => {
    setCourseForm({
      id: course.id,
      name: course.name,
      description: course.description || '',
      audience: course.audience || '',
      status: course.status,
      isForAllCompanies: course.isForAllCompanies,
      companyIds: course.isForAllCompanies ? [] : course.companyIds,
      curatorIds: course.curatorIds || course.curators?.map((item) => item.id) || [],
    });
    setCourseFormError(null);
    setIsCourseDialogOpen(true);
  };

  const validateCourse = () => {
    if (!courseForm.name.trim()) return 'Укажите название курса';
    if (!courseForm.isForAllCompanies && courseForm.companyIds.length === 0) {
      return 'Выберите хотя бы одну компанию';
    }
    return null;
  };

  const handleSaveCourse = async () => {
    const validationError = validateCourse();
    if (validationError) {
      setCourseFormError(validationError);
      return;
    }

    if (curatorsLoadError && !courseForm.id) {
      setCourseFormError(
        'Нельзя создать курс: список кураторов не загружен. Обновите страницу.',
      );
      return;
    }

    setIsCourseSaving(true);
    setCourseFormError(null);

    const payload: Record<string, unknown> = {
      name: courseForm.name.trim(),
      description: courseForm.description.trim() || null,
      audience: courseForm.audience.trim() || null,
      status: courseForm.status,
      isForAllCompanies: courseForm.isForAllCompanies,
    };

    if (courseForm.id) payload.id = courseForm.id;
    if (!courseForm.isForAllCompanies) payload.companyIds = courseForm.companyIds;
    if (!curatorsLoadError) {
      payload.curatorIds = courseForm.curatorIds;
    }

    try {
      await saveCourse(payload);
      setIsCourseDialogOpen(false);
      await refreshCourses();
    } catch (e: unknown) {
      setCourseFormError(getErrorMessage(e, 'Ошибка сохранения курса'));
    } finally {
      setIsCourseSaving(false);
    }
  };

  const handleArchiveCourse = async () => {
    if (!archiveCourseId) return;

    setIsCourseArchiving(true);
    setArchiveCourseError(null);

    try {
      await archiveCourse(archiveCourseId);
      setArchiveCourseId(null);
      await refreshCourses();
    } catch (e: unknown) {
      setArchiveCourseError(getErrorMessage(e, 'Ошибка архивации курса'));
    } finally {
      setIsCourseArchiving(false);
    }
  };

  const handleRestoreCourse = async (id: string) => {
    setRestoringCourseId(id);
    setError(null);

    try {
      await restoreCourse(id);
      await refreshCourses();
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Ошибка восстановления курса'));
    } finally {
      setRestoringCourseId(null);
    }
  };

  const handleReorderCourse = async (id: string, direction: 'up' | 'down') => {
    const items = buildReorderItems(activeCourses, id, direction);
    if (!items) return;

    try {
      await reorderCourses(items);
      await refreshCourses();
    } catch (e: unknown) {
      setError(getErrorMessage(e, 'Ошибка изменения порядка курсов'));
    }
  };

  const getCourseIndex = (courseId: string) =>
    activeCourses.findIndex((course) => course.id === courseId);

  if (courseId) {
    return <ContentCourseDetail courseId={courseId} onBack={() => navigate('/content')} />;
  }

  const archiveCourseTarget = courses.find((course) => course.id === archiveCourseId);

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Обучение" />

      <Card sx={contentCardSx}>
        <CardHeader
          title="Курсы"
          action={
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={refreshCourses} disabled={isLoading}>
                Обновить
              </Button>
              <Button variant="contained" onClick={openCreateCourse}>
                Создать курс
              </Button>
            </Stack>
          }
        />

        {isLoading ? <LinearProgress /> : <Box sx={{ height: 4 }} />}

        <CardContent>
          {error && (
            <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'flex-end' },
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label="Поиск"
              placeholder="Название, описание, аудитория"
              value={filters.search}
              onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
              size="small"
              sx={{ flex: 1, minWidth: 0 }}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl size="small" sx={{ width: { xs: '100%', md: 160 }, flexShrink: 0 }}>
              <InputLabel id="course-status-filter-label">Статус</InputLabel>
              <Select
                labelId="course-status-filter-label"
                label="Статус"
                value={filters.status}
                onChange={(e) =>
                  setFilters((current) => ({
                    ...current,
                    status: e.target.value as CourseListFilters['status'],
                  }))
                }
              >
                <MenuItem value="all">Все</MenuItem>
                <MenuItem value="draft">Черновик</MenuItem>
                <MenuItem value="published">Опубликовано</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: { xs: '100%', md: 160 }, flexShrink: 0 }}>
              <InputLabel id="course-archive-filter-label">Архив</InputLabel>
              <Select
                labelId="course-archive-filter-label"
                label="Архив"
                value={archiveFilter}
                onChange={(e) => setArchiveFilter(e.target.value as CourseArchiveFilter)}
              >
                <MenuItem value="active">Активные</MenuItem>
                <MenuItem value="archived">В архиве</MenuItem>
                <MenuItem value="all">Все</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {courses.length === 0 && !isLoading ? (
            <Typography>Курсов пока нет</Typography>
          ) : filteredCourses.length === 0 ? (
            <Typography>Ничего не найдено по текущим фильтрам</Typography>
          ) : (
            <TableContainer sx={contentTableContainerSx}>
              <Table size="small" sx={contentTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={courseTableCellSx.index}>#</TableCell>
                    <TableCell sx={courseTableCellSx.name}>Название</TableCell>
                    <TableCell sx={courseTableCellSx.description}>Описание</TableCell>
                    <TableCell sx={courseTableCellSx.count}>Папок</TableCell>
                    <TableCell sx={courseTableCellSx.count}>Видео</TableCell>
                    <TableCell sx={courseTableCellSx.audience}>Аудитория</TableCell>
                    <TableCell sx={courseTableCellSx.status}>Статус</TableCell>
                    <TableCell sx={courseTableCellSx.access}>Доступ</TableCell>
                    <TableCell sx={courseTableCellSx.date}>Создан</TableCell>
                    <TableCell sx={courseTableCellSx.date}>Изменён</TableCell>
                    <TableCell sx={courseTableCellSx.id}>ID</TableCell>
                    <TableCell sx={courseTableCellSx.actions}>
                      Действия
                    </TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {filteredCourses.map((course) => {
                  const isArchived = isCourseArchived(course);
                  const courseIndex = getCourseIndex(course.id);
                  const accessLabel = getAccessLabel(course.isForAllCompanies, course.companyIds);
                  const companyNames = getCompanyNames(course.companyIds, companies);

                  return (
                  <TableRow key={course.id} hover sx={isArchived ? { opacity: 0.85 } : undefined}>
                    <TableCell sx={courseTableCellSx.index}>{isArchived ? '—' : courseIndex + 1}</TableCell>
                    <TableCell sx={courseTableCellSx.name}>
                      <Stack direction="row" spacing={0.5} alignItems="flex-start" flexWrap="wrap">
                        <Typography component="span">{course.name}</Typography>
                        {isArchived && (
                          <Chip size="small" color="warning" label="Архив" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={courseTableCellSx.description}>
                      {course.description ? (
                        <Typography component="span">{course.description}</Typography>
                      ) : (
                        <Typography component="span" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={courseTableCellSx.count}>{course.foldersCount ?? '—'}</TableCell>
                    <TableCell sx={courseTableCellSx.count}>{course.videosCount ?? '—'}</TableCell>
                    <TableCell sx={courseTableCellSx.audience}>
                      <Typography noWrap title={course.audience || undefined}>
                        {course.audience || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={courseTableCellSx.status}>
                      <Chip
                        size="small"
                        label={formatStatus(course.status)}
                        color={course.status === 'published' ? 'success' : 'default'}
                        variant={course.status === 'published' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={courseTableCellSx.access}>
                      {!course.isForAllCompanies && companyNames ? (
                        <Tooltip title={companyNames}>
                          <Typography component="span" sx={{ cursor: 'help' }}>
                            {accessLabel}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography component="span">{accessLabel}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={courseTableCellSx.date}>
                      {formatTableDateTime(course.createdAt)}
                    </TableCell>
                    <TableCell sx={courseTableCellSx.date}>
                      {formatTableDateTime(course.updatedAt)}
                    </TableCell>
                    <TableCell sx={courseTableCellSx.id}>
                      <CopyIdButton id={course.id} />
                    </TableCell>
                    <TableCell sx={courseTableCellSx.actions}>
                      <Stack direction="column" {...actionColumnStackSx}>
                        {!isArchived && (
                          <Stack {...actionReorderRowSx}>
                            <Button
                              size="small"
                              sx={reorderButtonSx}
                              disabled={courseIndex <= 0}
                              onClick={() => handleReorderCourse(course.id, 'up')}
                            >
                              ↑
                            </Button>
                            <Button
                              size="small"
                              sx={reorderButtonSx}
                              disabled={courseIndex < 0 || courseIndex >= activeCourses.length - 1}
                              onClick={() => handleReorderCourse(course.id, 'down')}
                            >
                              ↓
                            </Button>
                          </Stack>
                        )}
                        <Button
                          size="small"
                          sx={actionButtonColumnSx}
                          onClick={() => openCourse(course.id)}
                        >
                          Открыть
                        </Button>
                        {!isArchived && (
                          <Button
                            size="small"
                            sx={actionButtonColumnSx}
                            onClick={() => openEditCourse(course)}
                          >
                            Изменить
                          </Button>
                        )}
                        {isArchived ? (
                          <Button
                            size="small"
                            color="warning"
                            sx={actionButtonColumnSx}
                            disabled={restoringCourseId === course.id}
                            onClick={() => handleRestoreCourse(course.id)}
                          >
                            Восстановить
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            color="warning"
                            sx={actionButtonColumnSx}
                            onClick={() => {
                              setArchiveCourseId(course.id);
                              setArchiveCourseError(null);
                            }}
                          >
                            В архив
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <CourseFormDialog
        open={isCourseDialogOpen}
        form={courseForm}
        companies={companies}
        curators={curators}
        curatorsLoadError={curatorsLoadError}
        isSaving={isCourseSaving}
        error={courseFormError}
        onClose={() => !isCourseSaving && setIsCourseDialogOpen(false)}
        onSave={handleSaveCourse}
        onChange={setCourseForm}
      />

      <ConfirmDeleteDialog
        open={Boolean(archiveCourseId)}
        title="Переместить курс в архив?"
        description={
          archiveCourseTarget
            ? `Курс «${archiveCourseTarget.name}» скроется в B2B ЛК. Папки, видео и прогресс сотрудников сохранятся. Курс можно восстановить.`
            : 'Курс будет перемещён в архив.'
        }
        isDeleting={isCourseArchiving}
        error={archiveCourseError}
        onClose={() => !isCourseArchiving && setArchiveCourseId(null)}
        onConfirm={handleArchiveCourse}
        confirmLabel="В архив"
      />
    </Box>
  );
};

export default Content;
